import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useTranslation } from '@/contexts/TranslationContext'
import { updateProfile } from '@/lib/services/profiles'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
  loadingLabel: string
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>('light')
  const [mounted, setMounted] = useState(false)
  const { user: authUser } = useAuth()
  const { t } = useTranslation()

  useEffect(() => {
    setMounted(true)
    // Carregar tema do localStorage
    const savedTheme = localStorage.getItem('theme') as Theme
    if (savedTheme) {
      setTheme(savedTheme)
      document.documentElement.classList.toggle('dark', savedTheme === 'dark')
    }
  }, [])

  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
    localStorage.setItem('theme', newTheme)
    document.documentElement.classList.toggle('dark', newTheme === 'dark')

    // Salvar no perfil do usuário
    try {
      if (authUser?.id) {
        await updateProfile(authUser.id, { theme: newTheme })
      }
    } catch (error) {
      console.error('Erro ao salvar tema:', error)
    }
  }

  if (!mounted) {
    return null
  }

  const loadingLabel = t ? t('common.loading') : 'Loading...'

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, loadingLabel }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
